import React, { useEffect, useRef, useState } from 'react';

export interface SearchSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  emptyText?: string;
  required?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const DEFAULT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)',
  borderRadius: 8,
  fontSize: 13,
  color: 'var(--nx-text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * A type-to-filter dropdown for picking one value out of a potentially long
 * list (e.g. residents), where the stored value (an id) differs from the
 * label shown to the user — a plain <select>/<datalist> can't do that split.
 */
const SearchSelect: React.FC<Props> = ({
  value, onChange, options, placeholder = 'Search…', emptyText = 'No matches', required, disabled, style,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);
  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  const pick = (opt: SearchSelectOption) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]); return; }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        value={open ? query : (selected?.label ?? '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required && !value}
        disabled={disabled}
        style={{ ...DEFAULT_STYLE, ...style }}
        autoComplete="off"
      />
      {open && (
        <div
          style={{
            position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
            maxHeight: 220, overflowY: 'auto',
            background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--nx-text-muted)' }}>{emptyText}</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                onMouseDown={e => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                  background: i === highlight ? 'var(--nx-blue)' : 'transparent',
                  color: i === highlight ? '#fff' : 'var(--nx-text-primary)',
                }}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
